import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { isDefined } from '@morpho-org/morpho-ts';
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import { vaultIncludesDonations } from '@/config/donation-vaults';
import { formatAdjustedAmount } from '@/helpers/formatters';
import { isVaultV2RateCapped } from '@/helpers/vaultV2RateCap';
import { useVaultV2Apy } from '@/hooks/useVaultV2Apy';
import { useVaultV2RecapData, type VaultV2RecapSource } from './useVaultV2RecapData';
export const useVaultV2RecapFormatting = t0 => {
  const $ = _c(48);
  const {
    vaultAddress,
    initialPosition,
    finalPosition,
    source
  } = t0;
  let t1;
  if ($[0] !== source || $[1] !== vaultAddress) {
    t1 = {
      vaultAddress,
      source
    };
    $[0] = source;
    $[1] = vaultAddress;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    vaultV2,
    vaultAsset,
    loading
  } = useVaultV2RecapData(t1);
  let t2;
  if ($[3] !== vaultV2) {
    t2 = {
      vaultV2
    };
    $[3] = vaultV2;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    apy,
    netApy,
    performanceFee,
    managementFee,
    points,
    rewards,
    maxRate
  } = useVaultV2Apy(t2);
  const grossApy = vaultV2?.apy ?? null;
  const maxApy = vaultV2?.maxApy ?? null;
  let t3;
  if ($[5] !== grossApy || $[6] !== maxApy) {
    t3 = isVaultV2RateCapped(grossApy, maxApy);
    $[5] = grossApy;
    $[6] = maxApy;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const isRateCapped = t3;
  let t4;
  if ($[8] !== vaultV2) {
    t4 = vaultV2 ? vaultIncludesDonations(vaultV2.asset.chain.id, vaultV2.address) : false;
    $[8] = vaultV2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const includesDonations = t4;
  let t5;
  if ($[10] !== netApy) {
    t5 = formatters.percent.digits(2).default("-").unit("%").max(10).of(netApy, 18);
    $[10] = netApy;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const formattedNetApy = t5;
  let t6;
  if ($[12] !== vaultAsset) {
    t6 = position => isDefined(vaultAsset) ? formatters.commas.digits(getDigitsFromPriceAndAmount(vaultAsset.price, position)).trailingZero(!position).of(position, vaultAsset.decimals) : "-";
    $[12] = vaultAsset;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const formatPosition = t6;
  let t7;
  if ($[14] !== vaultAsset) {
    t7 = position_0 => position_0 && isDefined(vaultAsset) ? formatAdjustedAmount({
      price: vaultAsset.price,
      amount: position_0,
      decimals: vaultAsset.decimals,
      withTrailingZero: !position_0,
      symbol: vaultAsset.symbol
    }) : undefined;
    $[14] = vaultAsset;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const formatDetailedPosition = t7;
  let t8;
  if ($[16] !== formatPosition || $[17] !== initialPosition) {
    t8 = formatPosition(initialPosition);
    $[16] = formatPosition;
    $[17] = initialPosition;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const formattedInitialPosition = t8;
  let t9;
  if ($[19] !== finalPosition || $[20] !== formatPosition) {
    t9 = formatPosition(finalPosition);
    $[19] = finalPosition;
    $[20] = formatPosition;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const formattedFinalPosition = t9;
  let t10;
  if ($[22] !== formatDetailedPosition || $[23] !== initialPosition) {
    t10 = formatDetailedPosition(initialPosition);
    $[22] = formatDetailedPosition;
    $[23] = initialPosition;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const detailedFormattedInitialPosition = t10;
  let t11;
  if ($[25] !== finalPosition || $[26] !== formatDetailedPosition) {
    t11 = formatDetailedPosition(finalPosition);
    $[25] = finalPosition;
    $[26] = formatDetailedPosition;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const detailedFormattedFinalPosition = t11;
  let t12;
  if ($[28] !== apy || $[29] !== detailedFormattedFinalPosition || $[30] !== detailedFormattedInitialPosition || $[31] !== formattedFinalPosition || $[32] !== formattedInitialPosition || $[33] !== formattedNetApy || $[34] !== grossApy || $[35] !== includesDonations || $[36] !== isRateCapped || $[37] !== loading || $[38] !== managementFee || $[39] !== maxApy || $[40] !== maxRate || $[41] !== netApy || $[42] !== performanceFee || $[43] !== points || $[44] !== rewards || $[45] !== vaultAsset || $[46] !== vaultV2) {
    t12 = {
      vaultV2,
      vaultAsset,
      apy,
      performanceFee,
      managementFee,
      netApy,
      formattedNetApy,
      rewards,
      points,
      grossApy,
      maxApy,
      isRateCapped,
      includesDonations,
      formattedInitialPosition,
      detailedFormattedInitialPosition,
      formattedFinalPosition,
      detailedFormattedFinalPosition,
      loading,
      maxRate
    };
    $[28] = apy;
    $[29] = detailedFormattedFinalPosition;
    $[30] = detailedFormattedInitialPosition;
    $[31] = formattedFinalPosition;
    $[32] = formattedInitialPosition;
    $[33] = formattedNetApy;
    $[34] = grossApy;
    $[35] = includesDonations;
    $[36] = isRateCapped;
    $[37] = loading;
    $[38] = managementFee;
    $[39] = maxApy;
    $[40] = maxRate;
    $[41] = netApy;
    $[42] = performanceFee;
    $[43] = points;
    $[44] = rewards;
    $[45] = vaultAsset;
    $[46] = vaultV2;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  return t12;
};